import * as React from "react";
import {
    Box,
    Chip,
    ClickAwayListener,
    Paper,
    Popper,
    Typography,
} from "@mui/material";
import { Close, ExpandLess, ExpandMore } from "@mui/icons-material";
import { StyledMenuItemContainer } from "../../../../components/commonComponents/StyledComponents/styled";

const RED = "#AF3241";

// Normalizes an email string for consistent comparison, stripping "New:" and trimming whitespace.
const normalizeEmailValue = (email) =>
    String(email || "")
        .replace(/^New:\s*/i, "")
        .replace(/:\s*\(Already Shared\)$/i, "")
        .trim()
        .toLowerCase();

// Splits comma-separated input into individual email values.
const parseEmailInput = (input) =>
    String(input || "")
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email !== "");

const EmailRecipientSelector = ({
    value,
    options,
    sharedEmails,
    onChange,
    label,
    fieldError,
    inputId,
    inputName,
    noOptionsText = "No matches found",
}) => {
    const anchorRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const optionRefs = React.useRef({});
    // Browser autofill keys off stable field names even when autocomplete is off.
    const browserAutocompleteName = React.useRef(
        `${inputName || "email_recipient"}_${Math.random().toString(36).slice(2)}`
    );

    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [isFocused, setIsFocused] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

    // Normalizes the selected values for consistent comparison.
    const selectedValues = React.useMemo(() => (
        Array.isArray(value) ? value : []
    ), [value]);

    // Shared emails should be excluded from selectable options, but still need to be checked against free-text input and shown as disabled if already shared.
    const availableOptions = React.useMemo(() => (
        Array.isArray(options) ? options : []
    ), [options]);

    // Normalizes the shared email values for consistent comparison.
    const sharedEmailValues = React.useMemo(() => (
        Array.isArray(sharedEmails) ? sharedEmails : []
    ), [sharedEmails]);

    const shouldShrinkLabel =
        isFocused || open || inputValue.length > 0 || selectedValues.length > 0;

    // Recreates the old Autocomplete filtering: matching favorites, "New:" rows,
    // already-shared rows, and hiding values that are already selected.
    const filteredOptions = React.useMemo(() => {
        const normalizedInputValue = inputValue.trim();
        const parsedInputValues = parseEmailInput(inputValue);
        const selectedEmailValues = selectedValues.map(normalizeEmailValue);

        // First filter the available options based on the input value.
        let filtered = availableOptions.filter((option) =>
            option.toLowerCase().indexOf(normalizedInputValue.toLowerCase()) !== -1
        );

        // Then, if the input value is non-empty and not already an existing option, add it as a "New:" option or an "(Already Shared)" option.
        const isExisting = availableOptions.some(
            (option) => option.trim().toLowerCase() === normalizedInputValue.toLowerCase()
        );

        // Only show the "Already Shared" marker if the input matches an already-shared email, regardless of whether it matches an available option. This covers the case where a user types in a free-text email that happens to already be shared.
        if (normalizedInputValue !== "" && parsedInputValues.length > 0 && !isExisting) {
            const isAlreadyShared = parsedInputValues.some((inputEmail) =>
                sharedEmailValues.some(
                    (email) => normalizeEmailValue(email) === normalizeEmailValue(inputEmail)
                )
            );

            if (isAlreadyShared) {
                filtered = [`${normalizedInputValue}: (Already Shared)`, ...filtered];
            } else {
                filtered = [`New: ${parsedInputValues.join(", ")}`, ...filtered];
            }
        }

        // Finally, remove any options that are already selected
        return filtered.filter((option) => !selectedEmailValues.includes(normalizeEmailValue(option)));
    }, [availableOptions, inputValue, selectedValues, sharedEmailValues]);

    // 
    React.useEffect(() => {
        setHighlightedIndex(-1);
    }, [inputValue]);

    const closeDropdown = React.useCallback(() => {
        setOpen(false);
        setHighlightedIndex(-1);
    }, []);

    // 
    const commitChange = React.useCallback(
        (event, nextValue, action) => {
            // Keep this component controlled; EmailSelection owns Redux updates.
            onChange(event, nextValue, action);
            setInputValue("");
            closeDropdown();
        },
        [closeDropdown, onChange]
    );

    const focusInput = React.useCallback(() => {
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    // 
    const selectOption = React.useCallback(
        (event, option) => {
            if (!option) return;
            if (/^New:\s*/i.test(option)) {
                const parsedValues = parseEmailInput(option.replace(/^New:\s*/i, ""));
                if (parsedValues.length === 0) return;
                commitChange(event, [...selectedValues, ...parsedValues], "createOption");
                focusInput();
                return;
            }
            commitChange(event, [...selectedValues, option], "selectOption");
            focusInput();
        },
        [commitChange, focusInput, selectedValues]
    );

    const createOption = React.useCallback(
        (event) => {
            const parsedValues = parseEmailInput(inputValue);
            if (parsedValues.length === 0) return;
            commitChange(event, [...selectedValues, ...parsedValues], "createOption");
            focusInput();
        },
        [commitChange, focusInput, inputValue, selectedValues]
    );

    // 
    const removeOptionAtIndex = React.useCallback(
        (event, optionIndex) => {
            event.stopPropagation();
            const nextValue = selectedValues.filter((_, index) => index !== optionIndex);
            commitChange(event, nextValue, "removeOption");
            focusInput();
        },
        [commitChange, focusInput, selectedValues]
    );

    const clearSelection = React.useCallback(
        (event) => {
            event.stopPropagation();
            commitChange(event, [], "clear");
            focusInput();
        },
        [commitChange, focusInput]
    );

    const scrollHighlightedIntoView = React.useCallback((index) => {
        const element = optionRefs.current[`option-${index}`];
        if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, []);

    const handleKeyDown = React.useCallback(
        (event) => {
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    setOpen(true);
                    if (filteredOptions.length === 0) return;
                    setHighlightedIndex((prev) => {
                        const next = prev < filteredOptions.length - 1 ? prev + 1 : 0;
                        setTimeout(() => scrollHighlightedIntoView(next), 0);
                        return next;
                    });
                    break;

                case "ArrowUp":
                    event.preventDefault();
                    setOpen(true);
                    if (filteredOptions.length === 0) return;
                    setHighlightedIndex((prev) => {
                        const next = prev > 0 ? prev - 1 : filteredOptions.length - 1;
                        setTimeout(() => scrollHighlightedIntoView(next), 0);
                        return next;
                    });
                    break;

                case "Enter":
                    // Enter selects the highlighted option, or creates a free-text email.
                    if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                        event.preventDefault();
                        selectOption(event, filteredOptions[highlightedIndex]);
                    } else if (inputValue.trim() !== "") {
                        event.preventDefault();
                        createOption(event);
                    }
                    break;

                case "Backspace":
                    // Match chip-input behavior: remove the last chip from an empty input.
                    if (inputValue === "" && selectedValues.length > 0) {
                        event.preventDefault();
                        const nextValue = selectedValues.slice(0, -1);
                        commitChange(event, nextValue, "removeOption");
                    }
                    break;

                case "Escape":
                    event.preventDefault();
                    closeDropdown();
                    break;

                default:
                    break;
            }
        },
        [
            closeDropdown,
            commitChange,
            createOption,
            filteredOptions,
            highlightedIndex,
            inputValue,
            scrollHighlightedIntoView,
            selectOption,
            selectedValues,
        ]
    );

    return (
        <ClickAwayListener onClickAway={closeDropdown}>
            <Box sx={{ width: "100%", position: "relative" }}>
                <Box
                    ref={anchorRef}
                    role="combobox"
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    onClick={() => {
                        setOpen(true);
                        focusInput();
                    }}
                    sx={{
                        position: "relative",
                        pt: 2.1,
                        borderBottom: `1px solid ${RED}`,
                        cursor: "text",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            bottom: -1,
                            width: isFocused || open ? "100%" : "0%",
                            height: "2px",
                            backgroundColor: RED,
                            transition: "width 180ms ease",
                        },
                    }}
                >
                    <Box
                        onClick={(event) => {
                            event.stopPropagation();
                            focusInput();
                        }}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: shouldShrinkLabel ? 0 : 22,
                            fontSize: shouldShrinkLabel ? 12 : 14,
                            color: RED,
                            transformOrigin: "top left",
                            transition: "all 160ms ease",
                            zIndex: 1,
                        }}
                    >
                        {label}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minHeight: 32,
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 0.5,
                                py: 0.5,
                            }}
                        >
                            {/* Selected recipients render as removable chips, replacing Autocomplete tags. */}
                            {selectedValues.map((option, index) => (
                                <Chip
                                    key={`${option}-${index}`}
                                    variant="filled"
                                    label={option}
                                    size="small"
                                    onDelete={(event) => removeOptionAtIndex(event, index)}
                                    sx={{
                                        backgroundColor: RED,
                                        color: "#FFFFFF",
                                        "& .MuiChip-deleteIcon": {
                                            color: "#FFFFFF",
                                        },
                                    }}
                                />
                            ))}
                            <Box
                                component="input"
                                ref={inputRef}
                                id={inputId}
                                name={browserAutocompleteName.current}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                aria-autocomplete="list"
                                data-lpignore="true"
                                data-1p-ignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                data-field-name={inputName}
                                value={inputValue}
                                onFocus={() => {
                                    setIsFocused(true);
                                    setOpen(true);
                                }}
                                onBlur={() => setIsFocused(false)}
                                onChange={(event) => {
                                    setInputValue(event.target.value);
                                    setOpen(true);
                                }}
                                onKeyDown={handleKeyDown}
                                sx={{
                                    flex: "1 1 140px",
                                    minWidth: 80,
                                    border: 0,
                                    outline: "none",
                                    color: RED,
                                    font: "inherit",
                                    fontSize: "13px",
                                    backgroundColor: "transparent",
                                    p: 0,
                                    lineHeight: "24px",
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "center",
                                // color: RED,
                            }}
                        >
                            {selectedValues.length > 0 && (
                                <Close
                                    fontSize="small"
                                    onClick={clearSelection}
                                    sx={{ cursor: "pointer", fontSize: 18 }}
                                />
                            )}
                            {open ? (
                                <ExpandLess
                                    fontSize="small"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        closeDropdown();
                                    }}
                                    sx={{ cursor: "pointer" }}
                                />
                            ) : (
                                <ExpandMore
                                    fontSize="small"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setOpen(true);
                                        focusInput();
                                    }}
                                    sx={{ cursor: "pointer" }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                {fieldError && (
                    <Typography
                        variant="caption"
                        sx={{ color: RED, mt: 0.5, display: "block" }}
                    >
                        {fieldError}
                    </Typography>
                )}

                {/* Popper-backed menu replaces Autocomplete's suggestions list. */}
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    sx={{ zIndex: 1400 }}
                    style={{
                        width: anchorRef.current?.offsetWidth,
                    }}
                >
                    <Paper
                        role="listbox"
                        sx={{
                            maxHeight: 220,
                            overflowY: "auto",
                            overflowX: "hidden",
                            mt: 0.5,
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => {
                                const isAlreadyShared = option.includes(": (Already Shared)");
                                return (
                                    <StyledMenuItemContainer
                                        key={option}
                                        ref={(element) => {
                                            optionRefs.current[`option-${index}`] = element;
                                        }}
                                        role="option"
                                        onClick={(event) => selectOption(event, option)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            backgroundColor:
                                                highlightedIndex === index
                                                    ? "rgba(175, 50, 65, 0.08)"
                                                    : "transparent",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "break-word",
                                                color: isAlreadyShared ? RED : "inherit",
                                            }}
                                        >
                                            {option}
                                        </Typography>
                                    </StyledMenuItemContainer>
                                );
                            })
                        ) : (
                            <Box sx={{ p: 2, textAlign: "center" }}>
                                <Typography variant="body2" color="textSecondary">
                                    {noOptionsText}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default EmailRecipientSelector;
